import {
  init,
  add,
  setUserId,
  identify,
  Identify,
  track as amplitudeTrack,
  type Types,
} from "@amplitude/analytics-browser";
import type { AnalyticsEventName, AnalyticsEventProps } from "./events";

export type {
  AnalyticsEventName,
  AnalyticsEventProps,
  AnalyticsSource,
} from "./events";

/**
 * Stamps `origin: "web"` on every event.
 *
 * Checkout reports into the same Amplitude project as the mobile app and uses
 * the same event names, so `origin` is the only thing separating the two
 * surfaces. The app stamps `origin: "app"` the same way, from its 27.2.0 native
 * release onwards — installs older than that send no `origin` at all, so chart
 * app traffic as `origin = app OR origin is not set`.
 *
 * Applied last so a call site can't overwrite it, matching the app.
 */
const originEnrichment: Types.EnrichmentPlugin = {
  name: "matchi-origin-enrichment",
  type: "enrichment",
  setup: async () => undefined,
  execute: async event => ({
    ...event,
    event_properties: { ...event.event_properties, origin: "web" },
  }),
};

const sha256 = async (value: string) => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return Array.from(new Uint8Array(digest), b =>
    b.toString(16).padStart(2, "0"),
  ).join("");
};

/**
 * Replaces `checkout_token` with its SHA-256 before an event leaves the page.
 *
 * The checkout token unlocks the order in the checkout API and lives for as
 * long as the order does, so it must not sit in a third-party analytics store.
 * The funnel only needs a join key: widget and checkout hash it the same way,
 * so their events still join on `checkout_token`. Anything that can't be
 * hashed (not a string, or no WebCrypto outside a secure context) is dropped,
 * never sent as is.
 */
const checkoutTokenEnrichment: Types.EnrichmentPlugin = {
  name: "matchi-checkout-token-enrichment",
  type: "enrichment",
  setup: async () => undefined,
  execute: async event => {
    const { checkout_token: token, ...props }: Record<string, unknown> = {
      ...event.event_properties,
    };
    if (token === undefined) return event;

    const hashed =
      typeof token === "string"
        ? await sha256(token).catch(() => undefined)
        : undefined;
    return {
      ...event,
      event_properties: hashed ? { ...props, checkout_token: hashed } : props,
    };
  },
};

/**
 * The Amplitude key, read once. `VITE_*` values are inlined at build time, so
 * this is a constant for the life of the bundle.
 *
 * Its absence — local dev and every Vercel preview — has to gate every entry
 * point below, not just `init`. An uninitialized client does not discard what
 * it is handed: `track` parks the event on `dispatchQ` behind a promise that
 * only settles once `init` runs, and `setUserId` queues onto `q` the same way.
 * Neither queue is ever drained in a keyless session, so guarding only `init`
 * would leave the tab holding one event and one pending promise per tracked
 * action for as long as it stays open.
 */
const apiKey = import.meta.env.VITE_AMPLITUDE_PLAYER_API_KEY;

export const initAnalytics = () => {
  // Send nothing rather than polluting the production project.
  if (!apiKey) return;

  add(originEnrichment);
  add(checkoutTokenEnrichment);

  // @see https://www.docs.developers.amplitude.com/data/sdks/browser-2/#configuration
  init(apiKey, {
    // Uncomment the logLevel line below to enable debug logging in development
    // logLevel:
    //   import.meta.env.MODE === "development"
    //     ? amplitude.Types.LogLevel.Debug
    //     : undefined,
    // Page views are tracked by hand (see trackPageView) so the payment token
    // can be stripped from the path. Everything else stays off so the events.ts
    // catalogue is an exhaustive list of what arrives, matching the mobile app.
    // @see https://www.docs.developers.amplitude.com/data/sdks/browser-2/#tracking-default-events
    // Remote config can replace `defaultTracking` from the Amplitude UI: a remote
    // `autocapture` object overrides these settings, and any key it leaves out
    // counts as on. Turned off so the settings below are the only ones in
    // force; nothing here uses remote config.
    fetchRemoteConfig: false,
    defaultTracking: {
      pageViews: false,
      sessions: true,
      attribution: false,
      formInteractions: false,
      fileDownloads: false,
    },
  });
};

/**
 * Typed `track`. The catalogue in ./events.ts is the source of truth — adding a
 * call here for an event that isn't in it is a type error.
 */
export const track = <K extends AnalyticsEventName>(
  event: K,
  props: AnalyticsEventProps[K],
) => {
  // Unkeyed builds queue forever rather than no-op — see the note on `apiKey`.
  if (!apiKey) return;

  amplitudeTrack(event, props);
};

/**
 * Identifies the signed-in user.
 *
 * User ID is the Keycloak ID — the same identity Sentry, the API and the mobile
 * app use, so a user found in one can be looked up in the others. The numeric
 * MATCHi id rides along as a user property for pivoting to what the backend and
 * staff quote.
 */
/**
 * The last value sent as `matchi_user_id`. Loaders re-run on every
 * revalidation and each `identify()` is its own event, so resending an
 * unchanged value is pure noise.
 */
let lastIdentifiedUserId: number | string | undefined;

export const identifyUser = ({
  keycloakId,
  matchiUserId,
}: {
  keycloakId: string;
  matchiUserId?: number | string;
}) => {
  // As in `track` — see the note on `apiKey`.
  if (!apiKey) return;

  setUserId(keycloakId);

  if (matchiUserId === undefined || matchiUserId === lastIdentifiedUserId) {
    return;
  }
  lastIdentifiedUserId = matchiUserId;

  const identifyEvent = new Identify();
  identifyEvent.set("matchi_user_id", String(matchiUserId));
  identify(identifyEvent);
};

/**
 * Collapses the payment token out of the path before tracking a page view —
 * `/pay/abc123/success` becomes `/pay/[token]/success`. Without this every page
 * view would carry a unique path and the property would be useless to group by.
 */
let lastTrackedPath: string | undefined;

export const trackPageView = (pathname: string) => {
  const path = pathname.replace(/^\/pay\/[^/]+/, "/pay/[token]");

  // Both call sites are React Router loaders, and loaders re-run on every
  // revalidation — `revalidate()` after a promo code or a value card
  // apply/unapply, the gift-card REVALIDATE_EVENT, and automatically after any
  // action that returns. Without this guard, one visit that applies and then
  // removes a value card reports four page views, which would make the first
  // step of the funnel unusable. Amplitude's autocapture only fired on history
  // changes, so this keeps parity with what it replaced.
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;

  track("Page Viewed", { path });
};
